import { CROUCH_HEIGHT, PLAYER_HEIGHT, PLAYER_RADIUS, PRONE_HEIGHT, Stance } from './constants.js';
import type { Vec3 } from './math.js';
import type { Aabb, World } from './sim.js';
import { rayHeightfield } from './terrain.js';

export interface HitTarget {
  id: number;
  /** Feet position. */
  pos: Vec3;
  crouching: boolean;
  /** constants.Stance; when present it overrides `crouching` for the capsule height. */
  stance?: number;
  /** Optional capsule height override (Hymns of different sizes). */
  height?: number;
}

export interface TraceResult {
  /** Distance along the ray to the first thing hit, or maxDist if nothing. */
  dist: number;
  point: Vec3;
  /** Player id hit, or 0 when the ray ended on world geometry / in the air. */
  targetId: number;
  headshot: boolean;
}

/** Unit direction from yaw/pitch using the same convention as the movement sim (yaw 0 = -Z). */
export function aimDirection(yaw: number, pitch: number): Vec3 {
  const cp = Math.cos(pitch);
  return { x: -Math.sin(yaw) * cp, y: Math.sin(pitch), z: -Math.cos(yaw) * cp };
}

/**
 * Head zone: the top slice of the capsule. Kept here rather than in `weapons.ts` (where it lived
 * while players had guns) because `traceRay` is the only thing that has ever read it, and a
 * tab-target game has no headshot rule for it to feed — it survives as trace metadata.
 */
export const HEAD_ZONE_HEIGHT = 0.25;

/**
 * Deterministic per-shot spread so two machines derive identical directions from
 * `(shooterId, shotSeq)`. It lived in `weapons.ts` while players had guns; it now belongs here,
 * with its only remaining caller — a Cantor's rail-lance wind-up.
 * Returns unit-square offsets in [-1, 1]^2, disc-filtered.
 */
export function shotSpread(shooterId: number, shotSeq: number): { x: number; y: number } {
  // xorshift-style hash; cheap and identical in every JS engine.
  let h = (shooterId * 374761393 + shotSeq * 668265263) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = (h * 1274126177) >>> 0;
  const a = ((h & 0xffff) / 0xffff) * Math.PI * 2;
  h = (h ^ (h >>> 16)) >>> 0;
  const r = Math.sqrt(((h >>> 8) & 0xffff) / 0xffff);
  return { x: Math.cos(a) * r, y: Math.sin(a) * r };
}

/** Apply deterministic spread to an aim direction. spreadDeg is the cone half-angle. */
export function spreadDirection(
  yaw: number,
  pitch: number,
  spreadDeg: number,
  playerId: number,
  shotSeq: number,
): Vec3 {
  const s = shotSpread(playerId, shotSeq);
  const rad = (spreadDeg * Math.PI) / 180;
  return aimDirection(yaw + s.x * rad, pitch + s.y * rad);
}

/** Player capsule height for hit purposes, by stance (or the legacy crouching boolean). */
export function targetHeight(stanceOrCrouching: number | boolean): number {
  if (typeof stanceOrCrouching === 'boolean')
    return stanceOrCrouching ? CROUCH_HEIGHT : PLAYER_HEIGHT;
  return stanceOrCrouching === Stance.Prone
    ? PRONE_HEIGHT
    : stanceOrCrouching === Stance.Crouch
      ? CROUCH_HEIGHT
      : PLAYER_HEIGHT;
}

function rayAabb(o: Vec3, d: Vec3, b: Aabb, maxDist: number): number {
  let tmin = 0;
  let tmax = maxDist;
  const axes: ('x' | 'y' | 'z')[] = ['x', 'y', 'z'];
  for (const a of axes) {
    const inv = 1 / d[a];
    let t1 = (b.min[a] - o[a]) * inv;
    let t2 = (b.max[a] - o[a]) * inv;
    if (t1 > t2) [t1, t2] = [t2, t1];
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return Infinity;
  }
  return tmin;
}

/**
 * Ray vs vertical capsule (axis-aligned Y). Returns distance or Infinity.
 * Capsule spans feet y..y+height with radius PLAYER_RADIUS; approximated as a cylinder with
 * hemispherical caps, which is exactly what CapsuleGeometry renders.
 */
function rayCapsule(o: Vec3, d: Vec3, feet: Vec3, height: number, maxDist: number): number {
  const r = PLAYER_RADIUS;
  const yA = feet.y + r;
  const yB = feet.y + height - r;
  // Infinite cylinder test in XZ.
  const ox = o.x - feet.x;
  const oz = o.z - feet.z;
  const a = d.x * d.x + d.z * d.z;
  let best = Infinity;
  if (a > 1e-9) {
    const b = 2 * (ox * d.x + oz * d.z);
    const c = ox * ox + oz * oz - r * r;
    const disc = b * b - 4 * a * c;
    if (disc >= 0) {
      const t = (-b - Math.sqrt(disc)) / (2 * a);
      if (t >= 0 && t <= maxDist) {
        const y = o.y + d.y * t;
        if (y >= yA && y <= yB) best = t;
      }
    }
  }
  // Sphere caps.
  for (const cy of [yA, yB]) {
    const sx = o.x - feet.x;
    const sy = o.y - cy;
    const sz = o.z - feet.z;
    const b = 2 * (sx * d.x + sy * d.y + sz * d.z);
    const c = sx * sx + sy * sy + sz * sz - r * r;
    const disc = b * b - 4 * c; // |d| = 1
    if (disc < 0) continue;
    const t = (-b - Math.sqrt(disc)) / 2;
    if (t >= 0 && t <= maxDist && t < best) best = t;
  }
  return best;
}

/**
 * Trace a hitscan ray against world boxes (occluders) and player capsules.
 * `targets` must exclude the shooter. Deterministic; runs identically on client and server.
 */
export function traceRay(
  origin: Vec3,
  dir: Vec3,
  maxDist: number,
  targets: readonly HitTarget[],
  world: World,
): TraceResult {
  let dist = maxDist;
  let targetId = 0;
  let headshot = false;
  for (const b of world.boxes) {
    const t = rayAabb(origin, dir, b, dist);
    if (t < dist) dist = t;
  }
  if (world.terrain) {
    const t = rayHeightfield(world.terrain, origin, dir, dist);
    if (t < dist) dist = t;
  } else if (dir.y < 0) {
    const t = (world.groundY - origin.y) / dir.y;
    if (t >= 0 && t < dist) dist = t;
  }
  for (const tg of targets) {
    const h = tg.height ?? targetHeight(tg.stance ?? tg.crouching);
    const t = rayCapsule(origin, dir, tg.pos, h, dist);
    if (t < dist) {
      dist = t;
      targetId = tg.id;
      const hitY = origin.y + dir.y * t;
      headshot = hitY >= tg.pos.y + h - HEAD_ZONE_HEIGHT;
    }
  }
  return {
    dist,
    point: { x: origin.x + dir.x * dist, y: origin.y + dir.y * dist, z: origin.z + dir.z * dist },
    targetId,
    headshot,
  };
}
